/**
 * The agent's nudge to name its session (RFC 0311 3b, S7).
 *
 * While a session's title is still the placeholder it was born with, the turn
 * carries one line saying so, beside `<holdings>` and `<view>`. Once anyone has
 * named it, the line is gone: a nudge that outlived its reason would be noise
 * on every turn after.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo'
import { awaitingName } from '../model/session/title'
import type { SessionMetaEntry } from '../model/session/types'

/** The `<session>` block for a registry entry, or '' when it has a real name. */
export function formatNamePrompt(entry: SessionMetaEntry | undefined): string {
  if (!awaitingName(entry)) return ''
  const title = String(entry?.title ?? '').trim()
  return [
    '<session>',
    `This session is still called ${title ? `"${title}"` : 'nothing'}, a placeholder. ` +
      'Once the work is clear, name it with name_session.',
    '</session>',
  ].join('\n')
}

/** How long to wait for the project document before leaving the line out. */
const NAME_TIMEOUT_MS = 5000

export async function readNamePrompt(
  repo: Repo,
  sessionId: string,
  projectId: string | null | undefined,
  opts: { timeoutMs?: number } = {},
): Promise<string> {
  if (!sessionId || !projectId) return ''
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const doc = await Promise.race([
      (async () => {
        const handle = await repo.find(projectId as AnyDocumentId)
        await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
        return handle.doc() as
          | { sessionMeta?: Record<string, SessionMetaEntry> }
          | undefined
      })(),
      new Promise<null>((resolve) => {
        timer = setTimeout(
          () => resolve(null),
          opts.timeoutMs ?? NAME_TIMEOUT_MS,
        )
        ;(timer as { unref?: () => void }).unref?.()
      }),
    ])
    return formatNamePrompt(doc?.sessionMeta?.[sessionId])
  } catch {
    return ''
  } finally {
    if (timer) clearTimeout(timer)
  }
}
